/**
 * Fonzie Companion Avatar - v1.0
 * Friendly avatar that appears on car kit pages
 * Opens video drawer when clicked
 */

class FonzieCompanion {
  constructor(config = {}) {
    this.engine = new FonzieEngine({ mode: 'companion', ...config });
    this.avatar = null;
    this.videoDrawer = null;
    this.currentVideo = null;
    this.isVisible = false;
    this.config = null; // Store config
    
    // Load configuration from customizer
    this.loadCustomizerConfig();
    
    this.init();
  }
  
  /**
   * Load configuration from Shopify customizer section
   */
  loadCustomizerConfig() {
    const configEl = document.getElementById('fonzie-builder-config');
    if (!configEl) {
      console.log('ℹ️ Fonzie Companion: No customizer config found');
      return;
    }
    
    try {
      this.config = JSON.parse(configEl.textContent);
      console.log('✅ Fonzie Companion: Customizer config loaded', this.config);
      
      // Apply video URLs from config
      if (this.config.videos) {
        Object.keys(this.config.videos).forEach(key => {
          if (this.config.videos[key]) {
            this.engine.videos[key] = this.config.videos[key];
          }
        });
        console.log('✅ Fonzie Companion: Video URLs updated', this.engine.videos);
      }
    } catch (e) {
      console.error('❌ Fonzie Companion: Failed to parse config', e);
    }
  }
  
  /**
   * Initialize companion
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  /**
   * Setup companion avatar
   */
  setup() {
    console.log('🤖 Fonzie Companion: Initializing...');
    
    // Check if companion is enabled in settings
    if (!this.isEnabled()) {
      console.log('🤖 Fonzie Companion: Disabled in settings, skipping');
      return;
    }
    
    // Check if we should show companion on this page
    if (!this.shouldShow()) {
      console.log('🤖 Fonzie Companion: Not a car kit page, skipping');
      return;
    }
    
    // Find avatar and video drawer elements
    this.avatar = document.querySelector('.fonzie-companion');
    this.videoDrawer = document.getElementById('fonzie-video-drawer');
    
    if (!this.avatar) {
      console.warn('⚠️ Fonzie Companion: Avatar element not found');
      return;
    }
    
    if (!this.videoDrawer) {
      console.warn('⚠️ Fonzie Companion: Video drawer not found');
      return;
    }
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Load products in background
    this.engine.loadCarKitProducts().catch(err => {
      console.error('❌ Fonzie Companion: Failed to load products', err);
    });
    
    // Show avatar after short delay
    setTimeout(() => {
      this.show();
    }, 1000);
    
    console.log('✅ Fonzie Companion: Ready');
  }
  
  /**
   * Check if companion is enabled in settings
   */
  isEnabled() {
    // If no config, assume disabled
    if (!this.config) {
      return false;
    }
    
    // Check if Fonzie system is enabled
    if (!this.config.enabled) {
      return false;
    }
    
    // Check if companion specifically is enabled
    if (!this.config.companionEnabled) {
      return false;
    }
    
    return true;
  }
  
  /**
   * Check if companion should show on current page
   */
  shouldShow() {
    // Check template type
    const isProductPage = document.body.classList.contains('template-product') ||
                          window.location.pathname.includes('/products/');
    const isCollectionPage = document.body.classList.contains('template-collection') ||
                              window.location.pathname.includes('/collections/');
    
    if (!isProductPage && !isCollectionPage) {
      return false;
    }
    
    // Check if it's a car kit product/collection
    const isCarKitProduct = document.querySelector('[data-product-type*="Car Kit"]') ||
                            document.querySelector('[data-product-type*="car kit"]') ||
                            document.querySelector('.ckb-section') || // Actual car kit builder class
                            document.querySelector('[id^="car-kit-builder-"]'); // Car kit builder ID pattern
    
    const isCarKitCollection = window.location.pathname.includes('car-kit') ||
                                window.location.pathname.includes('dipyourcar-peelable-car-kits');
    
    return isCarKitProduct || isCarKitCollection;
  }
  
  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Avatar click - open video drawer
    const avatarButton = this.avatar.querySelector('.fonzie-avatar');
    if (avatarButton) {
      avatarButton.addEventListener('click', () => {
        this.openVideoDrawer('welcome');
      });
    }
    
    // Video drawer close button
    const closeBtn = this.videoDrawer.querySelector('.fonzie-drawer-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeVideoDrawer();
      });
    }
    
    // Overlay click - close drawer
    const overlay = this.videoDrawer.querySelector('.fonzie-drawer-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.closeVideoDrawer();
      });
    }
    
    // Skip video button
    const skipBtn = this.videoDrawer.querySelector('.fonzie-skip-video');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        this.closeVideoDrawer();
      });
    }
    
    // Next step button (appears after video ends)
    const nextBtn = this.videoDrawer.querySelector('.fonzie-next-step');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.closeVideoDrawer();
        // Could trigger builder here
      });
    }
    
    // Keyboard: Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.videoDrawer.getAttribute('data-state') === 'open') {
        this.closeVideoDrawer();
      }
    });
    
    // Video player events
    const video = this.videoDrawer.querySelector('.fonzie-video-player');
    if (video) {
      // Show skip button after 5 seconds
      video.addEventListener('timeupdate', () => {
        if (video.currentTime >= 5) {
          const skipBtn = this.videoDrawer.querySelector('.fonzie-skip-video');
          if (skipBtn) {
            skipBtn.style.display = 'block';
          }
        }
      });
      
      // Show next step button when video ends
      video.addEventListener('ended', () => {
        const nextBtn = this.videoDrawer.querySelector('.fonzie-next-step');
        if (nextBtn) {
          nextBtn.style.display = 'block';
        }
        this.engine.trackEvent('video_completed', this.currentVideo);
      });
    }
  }
  
  /**
   * Show companion avatar
   */
  show() {
    if (this.isVisible || !this.avatar) return;
    
    this.avatar.classList.add('fonzie-visible');
    this.isVisible = true;
    
    this.engine.trackEvent('companion_shown', 'auto');
    console.log('👋 Fonzie Companion: Now visible');
  }
  
  /**
   * Hide companion avatar
   */
  hide() {
    if (!this.isVisible || !this.avatar) return;
    
    this.avatar.classList.remove('fonzie-visible');
    this.isVisible = false;
    
    console.log('👋 Fonzie Companion: Hidden');
  }
  
  /**
   * Open video drawer
   */
  openVideoDrawer(videoKey = 'welcome') {
    if (!this.videoDrawer) return;
    
    this.currentVideo = videoKey;
    
    // Get video source
    const videoUrl = this.engine.videos[videoKey];
    const video = this.videoDrawer.querySelector('.fonzie-video-player');
    const source = video ? video.querySelector('source') : null;
    
    if (!videoUrl || videoUrl === '') {
      console.warn('⚠️ Fonzie Companion: No video URL for', videoKey);
      // Show placeholder message
      this.showPlaceholderMessage(videoKey);
      return;
    }
    
    if (source) {
      source.src = videoUrl;
      if (video) {
        video.load();
      }
    }
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    
    // Open drawer
    this.videoDrawer.setAttribute('data-state', 'open');
    
    // Reset button visibility
    const skipBtn = this.videoDrawer.querySelector('.fonzie-skip-video');
    if (skipBtn) {
      skipBtn.style.display = 'none';
    }
    
    const nextBtn = this.videoDrawer.querySelector('.fonzie-next-step');
    if (nextBtn) {
      nextBtn.style.display = 'none';
    }
    
    // Play video
    if (video) {
      video.play().catch(err => {
        console.warn('⚠️ Video autoplay prevented:', err);
      });
    }
    
    // Track
    this.engine.trackEvent('video_opened', videoKey);
    console.log('🎬 Fonzie Companion: Video drawer opened');
  }
  
  /**
   * Show placeholder when videos not uploaded yet
   */
  showPlaceholderMessage(videoKey) {
    const videoWrapper = this.videoDrawer.querySelector('.fonzie-video-wrapper');
    if (!videoWrapper) return;
    
    const messages = {
      welcome: 'Hey! I\'m Fonzie from DipYourCar. Let\'s build your perfect car kit together!',
      color: 'Choose your color! We\'ve got over 100 options including Solid, Metallic, ColorShift, and HyperShift finishes.',
      size: 'Size matters! Pick based on your vehicle: Small for ATVs, Medium for sedans, Large for SUVs, and XL for trucks.',
      sprayer: 'Need a sprayer? Our G-Force is perfect for DIYers, or go Ultimate for the best finish. Already have one? Select "No thanks".',
      finish: 'Last step! Choose Standard for a classic matte look, or Ultra High Gloss for that premium wet finish.',
      complete: 'Your custom car kit is ready! Everything you need to transform your ride. Questions? We\'re here to help!'
    };
    
    videoWrapper.innerHTML = `
      <div style="
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #6DC3F7 0%, #0d1120 100%);
        color: white;
        padding: 32px;
        text-align: center;
        font-family: 'Karla', sans-serif;
      ">
        <div>
          <div style="font-size: 48px; margin-bottom: 16px;">👋</div>
          <p style="font-size: 18px; line-height: 1.6; margin: 0;">
            ${messages[videoKey] || 'Coming soon! Fonzie video will be here.'}
          </p>
        </div>
      </div>
    `;
    
    // Open drawer anyway
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    
    this.videoDrawer.setAttribute('data-state', 'open');
    
    this.engine.trackEvent('placeholder_shown', videoKey);
  }
  
  /**
   * Close video drawer
   */
  closeVideoDrawer() {
    if (!this.videoDrawer) return;
    
    // Stop video
    const video = this.videoDrawer.querySelector('.fonzie-video-player');
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    
    // Close drawer
    this.videoDrawer.setAttribute('data-state', 'closed');
    
    // Restore body scroll
    const scrollY = document.body.style.top;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
    
    // Track
    this.engine.trackEvent('video_closed', this.currentVideo);
    console.log('🎬 Fonzie Companion: Video drawer closed');
  }
  
  /**
   * Destroy companion
   */
  destroy() {
    this.hide();
    if (this.avatar) {
      this.avatar.remove();
    }
    if (this.videoDrawer) {
      this.videoDrawer.remove();
    }
    console.log('👋 Fonzie Companion: Destroyed');
  }
}

// Auto-initialize when engine is loaded
if (window.FonzieEngine) {
  // Wait for DOM to be fully ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (document.querySelector('[data-fonzie-companion="true"]')) {
        window.fonzieCompanion = new FonzieCompanion();
      }
    });
  } else {
    if (document.querySelector('[data-fonzie-companion="true"]')) {
      window.fonzieCompanion = new FonzieCompanion();
    }
  }
} else {
  console.warn('⚠️ Fonzie Companion: Engine not loaded yet');
}

console.log('✅ Fonzie Companion script loaded');

